import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // request | reset | done
  // 2026-08-08 — capture the default PIN returned by /reset-password so
  // the success screen can surface it (server now resets PIN alongside
  // password). Without this, users hit "invalid pin" on second-factor.
  const [resetPin, setResetPin] = useState("");
  const [loginId, setLoginId] = useState("");
  const [token, setToken] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverInfo, setServerInfo] = useState(null);

  const requestToken = async (e) => {
    e.preventDefault();
    if (!loginId.trim()) {
      toast.error("Enter your login ID");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, {
        login_id: loginId.trim(),
      });
      setServerInfo(data);
      if (data.reset_token) {
        setToken(data.reset_token);
      }
      setStep("reset");
      toast.success("Reset token generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start reset");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!token.trim() || !newPwd) {
      toast.error("Token and new password are required");
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/reset-password`, {
        token: token.trim(),
        new_password: newPwd,
      });
      setResetPin(resp?.data?.default_pin || "");
      setStep("done");
      toast.success(
        resp?.data?.default_pin
          ? `Password + PIN reset. Sign in with new password and PIN ${resp.data.default_pin}.`
          : "Password updated. Sign in with your new password."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[100dvh] bg-[#07111F] text-white flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="px-6 md:px-10 pt-8">
        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"
          data-testid="forgot-password-back"
        >
          <ArrowLeft size={14} />
          Back to login
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.22em] text-white/40 mb-3">
            Account recovery
          </div>
          <h1 className="text-3xl md:text-4xl font-light leading-tight">
            Forgot your{" "}
            <span className="italic text-[#C8A96B]">password?</span>
          </h1>

          {step === "request" && (
            <>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">
                Enter your login ID (email, PAN, sub-broker code, or
                <span className="font-mono text-[#C8A96B]"> SUPERUSER</span>).
                We'll generate a one-time reset token valid for 30 minutes.
              </p>
              <form onSubmit={requestToken} className="mt-8 space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-white/45 mb-2">
                    Login ID
                  </label>
                  <input
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    placeholder="your login ID"
                    data-testid="forgot-password-loginid"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C8A96B]/60 transition"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-request-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  Send reset token
                </button>
              </form>
              <div className="mt-6 text-xs text-white/45">
                Don't remember your login ID?{" "}
                <Link
                  to="/forgot-login-id"
                  className="text-[#C8A96B] hover:underline"
                >
                  Recover it here
                </Link>
              </div>
            </>
          )}

          {step === "reset" && (
            <>
              <div
                className="mt-6 border border-[#C8A96B]/40 bg-[#C8A96B]/[0.06] rounded-lg p-4 text-sm text-white/75 leading-relaxed"
                data-testid="forgot-password-info"
              >
                <div className="flex items-start gap-3">
                  <ShieldCheck size={16} className="text-[#C8A96B] mt-0.5 shrink-0" />
                  <div>
                    {serverInfo?.delivered_to && serverInfo.delivered_to !== "(no email on file)" ? (
                      <>
                        Reset link sent to{" "}
                        <span className="font-mono text-[#C8A96B]">
                          {serverInfo.delivered_to}
                        </span>
                        .
                      </>
                    ) : (
                      <>Reset token generated.</>
                    )}{" "}
                    Token expires at{" "}
                    {serverInfo?.expires_at ? (
                      <span className="font-mono text-white/80">
                        {new Date(serverInfo.expires_at).toLocaleTimeString()}
                      </span>
                    ) : (
                      "30 minutes from now"
                    )}.
                  </div>
                </div>
              </div>
              <form onSubmit={submitReset} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-white/45 mb-2">
                    Reset token
                  </label>
                  <input
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="paste the token from the email / above"
                    data-testid="forgot-password-token-input"
                    className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-[#C8A96B]/60 transition"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-white/45 mb-2">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      placeholder="8+ chars · 1 upper · 1 lower · 1 digit · 1 special"
                      data-testid="forgot-password-new-password"
                      className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 pr-10 text-white placeholder-white/30 focus:outline-none focus:border-[#C8A96B]/60 transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-reset-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  <KeyRound size={14} />
                  Update password
                </button>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="mt-8 border border-emerald-400/30 bg-emerald-400/[0.06] rounded-lg p-6" data-testid="forgot-password-done">
              <div className="flex items-start gap-3">
                <ShieldCheck size={20} className="text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-base font-medium text-white">
                    Password updated
                  </div>
                  <p className="mt-2 text-sm text-white/65">
                    You can now sign in with your new password.
                  </p>
                  {resetPin && (
                    <div
                      className="mt-4 rounded-md border border-amber-400/40 bg-amber-400/[0.08] px-4 py-3"
                      data-testid="forgot-password-pin-reset"
                    >
                      <div className="text-xs uppercase tracking-wide text-amber-200/85">
                        Your 4-digit PIN was also reset
                      </div>
                      <div className="mt-1 text-lg font-semibold text-amber-100">
                        PIN: <span className="font-mono tracking-widest">{resetPin}</span>
                      </div>
                      <p className="mt-1 text-xs text-white/60">
                        Please change this PIN immediately after signing in
                        (Settings → Change PIN).
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => navigate("/login")}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
                    data-testid="forgot-password-go-login"
                  >
                    Go to sign in
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
